<script setup lang="ts">
// Lichess-style elo-over-time line chart — hand-rolled inline SVG, NO charting library
// (DESIGN.md section 9.1, round-4 feedback item 2). Points are spaced evenly along X by match
// order, not real elapsed time — the same simplification Sparkline.vue already used for the old
// tiny sparkline, and what lichess's own rating graph does (each game is one evenly-spaced
// tick). A light grid with a couple of y-axis rating labels, a filled area under the line, and
// the first/last match dates in the corners stand in for lichess's hover tooltip — this kiosk
// is touch-only and a static chart reads fine at a glance without pointer interaction.
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    points: { at: string; value: number }[]
    width?: number
    height?: number
    stroke?: string
  }>(),
  { width: 560, height: 160, stroke: '#34d399' },
)

const PAD_LEFT = 34
const PAD_RIGHT = 8
const PAD_TOP = 12
const PAD_BOTTOM = 20

const plotWidth = computed(() => props.width - PAD_LEFT - PAD_RIGHT)
const plotHeight = computed(() => props.height - PAD_TOP - PAD_BOTTOM)

const bounds = computed(() => {
  const vals = props.points.map((p) => p.value)
  const rawMin = Math.min(...vals)
  const rawMax = Math.max(...vals)
  const span = rawMax - rawMin || 20
  const pad = Math.max(span * 0.12, 8)
  return { min: Math.floor(rawMin - pad), max: Math.ceil(rawMax + pad) }
})

function xAt(i: number): number {
  const n = props.points.length
  return PAD_LEFT + (n <= 1 ? 0 : (i / (n - 1)) * plotWidth.value)
}
function yAt(v: number): number {
  const { min, max } = bounds.value
  const range = max - min || 1
  return PAD_TOP + plotHeight.value - ((v - min) / range) * plotHeight.value
}

const linePath = computed(() =>
  props.points.map((p, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(p.value).toFixed(1)}`).join(' '),
)

const areaPath = computed(() => {
  if (props.points.length < 2) return ''
  const baseline = PAD_TOP + plotHeight.value
  const first = `M${xAt(0).toFixed(1)},${baseline.toFixed(1)}`
  const line = props.points.map((p, i) => `L${xAt(i).toFixed(1)},${yAt(p.value).toFixed(1)}`).join(' ')
  const last = `L${xAt(props.points.length - 1).toFixed(1)},${baseline.toFixed(1)} Z`
  return `${first} ${line} ${last}`
})

// max / mid / min — three horizontal grid lines with rating labels, lichess-style.
const gridLines = computed(() => {
  const { min, max } = bounds.value
  const mid = Math.round((min + max) / 2)
  return [max, mid, min]
})

const lastPoint = computed(() => {
  if (props.points.length === 0) return null
  const i = props.points.length - 1
  return { x: xAt(i), y: yAt(props.points[i].value) }
})

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
  } catch {
    return ''
  }
}

// Unique per instance so two charts on the same page (e.g. Rapid + Classic) don't share a
// <defs> gradient id.
const gradientId = `elo-grad-${Math.random().toString(36).slice(2, 9)}`
</script>

<template>
  <svg v-if="points.length >= 2" :width="width" :height="height" :viewBox="`0 0 ${width} ${height}`" class="overflow-visible w-full h-auto">
    <defs>
      <linearGradient :id="gradientId" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" :stop-color="stroke" stop-opacity="0.35" />
        <stop offset="100%" :stop-color="stroke" stop-opacity="0" />
      </linearGradient>
    </defs>
    <g v-for="g in gridLines" :key="g">
      <line :x1="PAD_LEFT" :x2="width - PAD_RIGHT" :y1="yAt(g)" :y2="yAt(g)" class="stroke-neutral-800" stroke-width="1" />
      <text :x="PAD_LEFT - 6" :y="yAt(g) + 3" text-anchor="end" class="fill-neutral-500" font-size="9" font-family="monospace">{{ g }}</text>
    </g>
    <path :d="areaPath" :fill="`url(#${gradientId})`" stroke="none" />
    <path :d="linePath" fill="none" :stroke="stroke" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
    <circle v-if="lastPoint" :cx="lastPoint.x" :cy="lastPoint.y" r="4" :fill="stroke" stroke="#0a0a0a" stroke-width="1.5" />
    <text v-if="points[0]" :x="PAD_LEFT" :y="height - 4" font-size="9" font-family="monospace" class="fill-neutral-600">
      {{ formatDate(points[0].at) }}
    </text>
    <text v-if="points.length" :x="width - PAD_RIGHT" :y="height - 4" text-anchor="end" font-size="9" font-family="monospace" class="fill-neutral-600">
      {{ formatDate(points[points.length - 1].at) }}
    </text>
  </svg>
  <div v-else class="text-sm text-neutral-500 flex items-center justify-center" :style="{ height: `${height}px` }">Not enough matches yet</div>
</template>
